import { Objective } from '../modes/MissionMode';

export class MissionHUD {
  private container: HTMLElement;
  private objectivesPanel: HTMLElement;
  private timerDisplay: HTMLElement;
  private briefingScreen: HTMLElement;
  private resultsScreen: HTMLElement;

  constructor() {
    this.container = this.createContainer();
    this.objectivesPanel = this.createObjectivesPanel();
    this.timerDisplay = this.createTimerDisplay();
    this.briefingScreen = this.createBriefingScreen();
    this.resultsScreen = this.createResultsScreen();

    this.container.appendChild(this.objectivesPanel);
    this.container.appendChild(this.timerDisplay);

    document.getElementById('ui-container')?.appendChild(this.container);
    document.getElementById('ui-container')?.appendChild(this.briefingScreen);
    document.getElementById('ui-container')?.appendChild(this.resultsScreen);

    this.hide();
  }

  private createContainer(): HTMLElement {
    const container = document.createElement('div');
    container.id = 'mission-hud';
    container.style.cssText = `
      position: absolute;
      top: 20px;
      left: 20px;
      display: flex;
      flex-direction: column;
      gap: 15px;
      font-family: 'Courier New', monospace;
      z-index: 100;
    `;
    return container;
  }

  private createObjectivesPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.style.cssText = `
      background: rgba(0, 0, 0, 0.7);
      border: 1px solid #00ffff;
      border-radius: 5px;
      padding: 15px;
      min-width: 250px;
    `;
    return panel;
  }

  private createTimerDisplay(): HTMLElement {
    const timer = document.createElement('div');
    timer.style.cssText = `
      color: #ff4444;
      font-size: 32px;
      font-weight: bold;
      text-shadow: 0 0 10px #ff4444;
      text-align: center;
      display: none;
    `;
    return timer;
  }

  private createBriefingScreen(): HTMLElement {
    const screen = document.createElement('div');
    screen.id = 'mission-briefing';
    screen.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 20px;
      z-index: 200;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
      font-family: 'Courier New', monospace;
    `;

    screen.innerHTML = `
      <div style="color: #00ffff; font-size: 16px; letter-spacing: 3px; margin-bottom: 10px;">
        MISSION BRIEFING
      </div>
      <div id="brief-title" style="color: #ffffff; font-size: 48px; font-weight: bold; text-shadow: 0 0 20px #ffffff; letter-spacing: 5px;">
        MISSION NAME
      </div>
      <div id="brief-text" style="color: #aaaaaa; font-size: 18px; max-width: 600px; text-align: center; line-height: 1.6; margin: 20px 0;">
        Mission description goes here...
      </div>
      <div style="color: #ffff00; font-size: 20px; margin-top: 20px; letter-spacing: 2px;">
        OBJECTIVES
      </div>
      <div id="brief-objectives" style="color: #ffffff; font-size: 16px; text-align: left;">
      </div>
      <div style="color: #00ff00; font-size: 20px; margin-top: 40px; letter-spacing: 3px; animation: pulse 1s infinite alternate;">
        PRESS SPACE TO BEGIN
      </div>
    `;

    return screen;
  }

  private createResultsScreen(): HTMLElement {
    const screen = document.createElement('div');
    screen.id = 'mission-results';
    screen.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 15px;
      z-index: 200;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.5s ease;
      font-family: 'Courier New', monospace;
    `;

    screen.innerHTML = `
      <div id="result-status" style="font-size: 64px; font-weight: bold; letter-spacing: 8px;">
        MISSION COMPLETE
      </div>
      <div id="result-title" style="color: #00ffff; font-size: 32px; margin-top: 20px;">
        Mission Name
      </div>
      <div id="result-time" style="color: #ffffff; font-size: 24px; margin-top: 20px;">
        Time: 00:00
      </div>
      <div id="result-kills" style="color: #ff8888; font-size: 20px;">
        Enemies Destroyed: 0
      </div>
      <div id="result-points" style="color: #ffff00; font-size: 28px; font-weight: bold; margin-top: 20px;">
        +0 POINTS
      </div>
      <div id="result-reason" style="color: #ff4444; font-size: 24px; display: none;">
      </div>
      <div id="result-controls" style="color: #888888; font-size: 16px; margin-top: 40px; letter-spacing: 2px;">
        PRESS ENTER TO RETRY | N FOR NEXT MISSION
      </div>
    `;

    return screen;
  }

  public show(): void {
    this.container.style.display = 'flex';
  }

  public hide(): void {
    this.container.style.display = 'none';
    this.hideBriefing();
    this.hideResults();
  }

  public showBriefing(title: string, text: string, objectives: string[]): void {
    const titleEl = this.briefingScreen.querySelector('#brief-title') as HTMLElement;
    const textEl = this.briefingScreen.querySelector('#brief-text') as HTMLElement;
    const objEl = this.briefingScreen.querySelector('#brief-objectives') as HTMLElement;

    titleEl.textContent = title.toUpperCase();
    textEl.textContent = text;

    let objHtml = '';
    objectives.forEach((obj) => {
      objHtml += `<div style="margin: 5px 0;">[ ] ${obj}</div>`;
    });
    objEl.innerHTML = objHtml;

    this.briefingScreen.style.opacity = '1';
    this.briefingScreen.style.pointerEvents = 'auto';
  }

  public hideBriefing(): void {
    this.briefingScreen.style.opacity = '0';
    this.briefingScreen.style.pointerEvents = 'none';
  }

  public showObjectives(objectives: Objective[]): void {
    let html = `
      <div style="color: #00ffff; font-size: 14px; margin-bottom: 10px; letter-spacing: 2px;">
        OBJECTIVES
      </div>
    `;

    for (const obj of objectives) {
      const checkmark = obj.completed ? '[X]' : '[ ]';
      const color = obj.completed ? '#00ff00' : '#ffffff';
      const opacity = obj.completed ? '0.6' : '1';

      let progress = '';
      if (obj.type === 'destroy' || obj.type === 'collect') {
        progress = ` (${obj.current}/${obj.target})`;
      } else if (obj.type === 'survive' || obj.type === 'protect') {
        const secs = Math.floor(obj.current);
        const targetSecs = Math.floor(obj.target);
        progress = ` (${secs}s/${targetSecs}s)`;
      }

      html += `
        <div style="color: ${color}; font-size: 14px; margin: 8px 0; opacity: ${opacity};">
          ${checkmark} ${obj.description}${progress}
        </div>
      `;
    }

    this.objectivesPanel.innerHTML = html;
  }

  public updateObjectives(objectives: Objective[]): void {
    this.showObjectives(objectives);
  }

  public showTimer(seconds: number): void {
    this.timerDisplay.style.display = 'block';
    this.updateTimer(seconds);
  }

  public updateTimer(seconds: number): void {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    this.timerDisplay.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;

    // Flash red when low
    if (seconds <= 10) {
      this.timerDisplay.style.color = '#ff0000';
      this.timerDisplay.style.textShadow = '0 0 15px #ff0000';
    } else {
      this.timerDisplay.style.color = '#ff4444';
      this.timerDisplay.style.textShadow = '0 0 10px #ff4444';
    }
  }

  public hideTimer(): void {
    this.timerDisplay.style.display = 'none';
  }

  public showMissionComplete(title: string, time: number, points: number, kills: number): void {
    const statusEl = this.resultsScreen.querySelector('#result-status') as HTMLElement;
    const titleEl = this.resultsScreen.querySelector('#result-title') as HTMLElement;
    const timeEl = this.resultsScreen.querySelector('#result-time') as HTMLElement;
    const killsEl = this.resultsScreen.querySelector('#result-kills') as HTMLElement;
    const pointsEl = this.resultsScreen.querySelector('#result-points') as HTMLElement;
    const reasonEl = this.resultsScreen.querySelector('#result-reason') as HTMLElement;
    const controlsEl = this.resultsScreen.querySelector('#result-controls') as HTMLElement;

    statusEl.textContent = 'MISSION COMPLETE';
    statusEl.style.color = '#00ff00';
    statusEl.style.textShadow = '0 0 30px #00ff00';

    titleEl.textContent = title;
    timeEl.textContent = `Time: ${this.formatTime(time)}`;
    killsEl.textContent = `Enemies Destroyed: ${kills}`;
    pointsEl.textContent = `+${points.toLocaleString()} POINTS`;
    reasonEl.style.display = 'none';
    controlsEl.textContent = 'PRESS ENTER TO REPLAY | N FOR NEXT MISSION';

    this.resultsScreen.style.opacity = '1';
    this.resultsScreen.style.pointerEvents = 'auto';
  }

  public showMissionFailed(title: string, reason: string): void {
    const statusEl = this.resultsScreen.querySelector('#result-status') as HTMLElement;
    const titleEl = this.resultsScreen.querySelector('#result-title') as HTMLElement;
    const timeEl = this.resultsScreen.querySelector('#result-time') as HTMLElement;
    const killsEl = this.resultsScreen.querySelector('#result-kills') as HTMLElement;
    const pointsEl = this.resultsScreen.querySelector('#result-points') as HTMLElement;
    const reasonEl = this.resultsScreen.querySelector('#result-reason') as HTMLElement;
    const controlsEl = this.resultsScreen.querySelector('#result-controls') as HTMLElement;

    statusEl.textContent = 'MISSION FAILED';
    statusEl.style.color = '#ff0000';
    statusEl.style.textShadow = '0 0 30px #ff0000';

    titleEl.textContent = title;
    timeEl.style.display = 'none';
    killsEl.style.display = 'none';
    pointsEl.style.display = 'none';
    reasonEl.textContent = reason;
    reasonEl.style.display = 'block';
    controlsEl.textContent = 'PRESS ENTER TO RETRY';

    this.resultsScreen.style.opacity = '1';
    this.resultsScreen.style.pointerEvents = 'auto';
  }

  public showAllComplete(): void {
    const statusEl = this.resultsScreen.querySelector('#result-status') as HTMLElement;
    const titleEl = this.resultsScreen.querySelector('#result-title') as HTMLElement;
    const timeEl = this.resultsScreen.querySelector('#result-time') as HTMLElement;
    const killsEl = this.resultsScreen.querySelector('#result-kills') as HTMLElement;
    const pointsEl = this.resultsScreen.querySelector('#result-points') as HTMLElement;
    const reasonEl = this.resultsScreen.querySelector('#result-reason') as HTMLElement;
    const controlsEl = this.resultsScreen.querySelector('#result-controls') as HTMLElement;

    statusEl.textContent = 'ALL MISSIONS COMPLETE';
    statusEl.style.color = '#ffff00';
    statusEl.style.textShadow = '0 0 30px #ffff00';

    titleEl.textContent = 'Congratulations, Pilot!';
    timeEl.style.display = 'none';
    killsEl.style.display = 'none';
    pointsEl.textContent = 'You are a true ace!';
    pointsEl.style.color = '#00ffff';
    reasonEl.style.display = 'none';
    controlsEl.textContent = 'PRESS M TO RETURN TO MENU';

    this.resultsScreen.style.opacity = '1';
    this.resultsScreen.style.pointerEvents = 'auto';
  }

  public hideResults(): void {
    this.resultsScreen.style.opacity = '0';
    this.resultsScreen.style.pointerEvents = 'none';

    // Reset visibility of elements
    const timeEl = this.resultsScreen.querySelector('#result-time') as HTMLElement;
    const killsEl = this.resultsScreen.querySelector('#result-kills') as HTMLElement;
    const pointsEl = this.resultsScreen.querySelector('#result-points') as HTMLElement;
    timeEl.style.display = 'block';
    killsEl.style.display = 'block';
    pointsEl.style.display = 'block';
    pointsEl.style.color = '#ffff00';
  }

  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  public destroy(): void {
    this.container.remove();
    this.briefingScreen.remove();
    this.resultsScreen.remove();
  }
}
